import { subscribe } from '../base/pubsub';

/**
 * Manages lead time display updates based on variant selection.
 * Subscribes to variant change events and updates the lead time text accordingly.
 */
class LeadTimeUpdater {
  /**
   * Creates a new LeadTimeUpdater instance.
   * @param {string} blockId - The unique identifier for the lead time block.
   * @param {Object} variantLeadTimes - Object mapping variant IDs to lead time data.
   * @param {string} leadTimeMessage - The base message to display before the lead time.
   */
  constructor(blockId, variantLeadTimes, leadTimeMessage, leadTimeMessageEnd) {
    this.blockId = blockId;
    this.variantLeadTimes = variantLeadTimes;
    this.leadTimeMessage = leadTimeMessage;
    this.leadTimeMessageEnd = leadTimeMessageEnd || '';
    this.unsubscribe = null;
    this.updateTimeout = null;
    this.init();
  }

  /**
   * Initialises the lead time updater by finding DOM elements and subscribing to events.
   * Sets up the variant change listener to update lead time display.
   */
  init() {
    // Find the lead time block element using the block ID.
    const leadTimeBlock = document.querySelector(`[data-lead-time-block="${this.blockId}"]`);
    if (!leadTimeBlock) return;

    // Find the text element that will display the lead time.
    this.leadTimeText = leadTimeBlock.querySelector('[data-lead-time-text]');
    if (!this.leadTimeText) return;

    // Subscribe to the variant change event using the theme's pub/sub system.
    this.unsubscribe = subscribe('variant-change', (data) => {
      // Early return if no valid variant data is present.
      if (!data.data || !data.data.variant) return;

      const variantId = data.data.variant.id;
      this.updateLeadTime(variantId);
    });
  }

  /**
   * Updates the lead time display text based on the selected variant.
   * @param {string} variantId - The ID of the selected variant.
   */
  updateLeadTime(variantId) {
    // Debounce rapid updates to improve performance.
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
    }

    this.updateTimeout = setTimeout(() => {
      try {
        // Get lead time data for the selected variant with proper validation.
        let leadTimeData = null;
        if (Object.prototype.hasOwnProperty.call(this.variantLeadTimes, variantId)) {
          leadTimeData = this.variantLeadTimes[`${variantId}`];
        }
        if (!leadTimeData) return;

        // Validate lead time data structure with smallest only.
        if (typeof leadTimeData.smallest !== 'number') {
          return;
        }

        // Use only the smallest lead time value for display.
        const { smallest } = leadTimeData;
        const suffix = this.leadTimeMessageEnd ? ` ${this.leadTimeMessageEnd}` : '';
        const dayText = smallest === 1 ? 'day' : 'days';
        const leadTimeTextContent = `${this.leadTimeMessage} ${smallest} ${dayText}${suffix}`;

        // Update the DOM element with the new lead time text.
        if (this.leadTimeText) {
          this.leadTimeText.textContent = leadTimeTextContent;
        }
      } catch {
        // Silently handle errors to prevent breaking the page.
      }
    }, 50);
    // 50ms debounce
  }

  /**
   * Cleans up event listeners and subscriptions to prevent memory leaks.
   * Should be called when the component is destroyed.
   */
  destroy() {
    if (this.updateTimeout) {
      clearTimeout(this.updateTimeout);
      this.updateTimeout = null;
    }

    if (this.unsubscribe) {
      this.unsubscribe();
      this.unsubscribe = null;
    }
  }
}

// Export for use in other modules.
export { LeadTimeUpdater };

/**
 * Auto-initialises LeadTimeUpdater instances for all lead time blocks on the page.
 * Looks for elements with data-lead-times-vary="true" and initialises updaters.
 */
function initializeLeadTimeUpdaters() {
  // Find all lead time blocks that have varying lead times.
  const leadTimeBlocks = document.querySelectorAll('[data-lead-times-vary="true"]');

  // Initialise a LeadTimeUpdater for each block.
  leadTimeBlocks.forEach((block) => {
    const blockId = block.dataset.leadTimeBlock;

    // Clean up existing instance if it exists.
    if (block.leadTimeUpdater) {
      block.leadTimeUpdater.destroy();
    }

    // Get data from window.leadTimeData if available.
    if (window.leadTimeData && Object.prototype.hasOwnProperty.call(window.leadTimeData, blockId)) {
      const data = window.leadTimeData[`${blockId}`];

      // Validate data structure.
      if (data && data.variants && data.message) {
        // Create and store the LeadTimeUpdater instance.
        const updater = new LeadTimeUpdater(blockId, data.variants, data.message, data.messageEnd);
        block.leadTimeUpdater = updater;
      }
    }
  });
}

// Initialize on DOM ready.
document.addEventListener('DOMContentLoaded', initializeLeadTimeUpdaters);

// Re-initialize when product info is updated (for AJAX updates).
document.addEventListener('product-info:loaded', initializeLeadTimeUpdaters);
